import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { IRect } from './placement';

/**
 * Time constant of the exponential approach, in milliseconds. Each frame the
 * displayed rect closes `1 - e^(-dt/TAU)` of whatever distance is left, so the
 * speed comes from the real elapsed time rather than the frame count — the
 * same glide on a 60Hz panel and a 120Hz one. A move is settled once every
 * edge is within half a pixel, which takes `ln(distance / 0.5) · TAU`:
 * ~330ms for a 500px jump, ~380ms for a full-viewport one.
 */
const TAU_MS = 50;
const SETTLED_PX = 0.5;

/** The largest edge-to-edge distance between two rects. */
const gap = (a: IRect, b: IRect): number =>
  Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y), Math.abs(a.w - b.w), Math.abs(a.h - b.h));

const approach = (from: IRect, to: IRect, progress: number): IRect => ({
  x: from.x + (to.x - from.x) * progress,
  y: from.y + (to.y - from.y) * progress,
  w: from.w + (to.w - from.w) * progress,
  h: from.h + (to.h - from.h) * progress,
});

export interface IEasedRect {
  /** The rectangle to draw this frame. Everything the tour paints — the hole
   *  in the sheet, the ring, the bulb's placement — derives from this one
   *  rect, so the three pieces can never drift apart. */
  rect: IRect | null;
  /** Re-measure, and glide toward the result. */
  follow: () => void;
}

export interface IEasedRectOptions {
  /** `prefers-reduced-motion`: land on the measurement instead of gliding. */
  reduced: boolean;
  /** Whether the caller still wants a rectangle at all — i.e. the step has a
   *  target, found or not. While this is true and the measurement is `null`,
   *  the loop keeps running and re-measuring: a target inside a dialog mounts
   *  a few frames after the step is entered, and a one-shot measurement would
   *  miss it and leave the step stranded as a centred card. */
  wantsRect: boolean;
}

/**
 * One rectangle that eases toward a measured target.
 *
 * `measure` is re-read on every frame the loop runs, not just when `follow` is
 * called: while the page smooth-scrolls to the next target the measurement
 * keeps moving, and the displayed rect keeps chasing it, so the spotlight
 * glides continuously instead of stepping between samples. The same re-read is
 * what lets a target that is not on the page yet be picked up as soon as it
 * appears.
 *
 * Snap, rather than ease, whenever there is nothing to ease between: no
 * displayed rect yet (a modal step into a target, or a target that just
 * appeared — it shows up where it belongs instead of flying in from a corner),
 * no target, or reduced motion.
 */
export function useEasedRect(measure: () => IRect | null, { reduced, wantsRect }: IEasedRectOptions): IEasedRect {
  const [rect, setRect] = useState<IRect | null>(null);
  const shown = useRef<IRect | null>(null);
  const measureRef = useRef(measure);
  const reducedRef = useRef(reduced);
  const wantsRef = useRef(wantsRect);
  const frameId = useRef(0);
  const lastTs = useRef<number | null>(null);

  // Written every render so a loop already in flight measures the CURRENT
  // step: a step change mid-glide re-aims the same loop at the new target
  // rather than restarting it, which is what keeps the movement continuous.
  useLayoutEffect(() => {
    measureRef.current = measure;
    reducedRef.current = reduced;
    wantsRef.current = wantsRect;
  });

  const commit = useCallback((next: IRect | null) => {
    shown.current = next;
    setRect(next);
  }, []);

  // 0 is not a live handle, and `cancelAnimationFrame` ignores one it doesn't
  // know — so this is safe to call whether or not a frame is pending.
  const stop = useCallback(() => {
    cancelAnimationFrame(frameId.current);
    frameId.current = 0;
  }, []);

  const start = useCallback(() => {
    if (frameId.current !== 0) return; // a loop is already running; it re-measures itself
    lastTs.current = null;
    const frame = (ts: number) => {
      const dt = lastTs.current === null ? 0 : ts - lastTs.current;
      lastTs.current = ts;
      const target = measureRef.current();
      // Nothing to point at yet (or any more). Show no ring and look again
      // next frame — the loop only ever runs while the step wants a rect, and
      // `follow` tears it down the moment that stops being true.
      if (target === null) {
        commit(null);
        frameId.current = requestAnimationFrame(frame);
        return;
      }
      const from = shown.current;
      // The target just appeared: land on it rather than fly in from wherever
      // the last step's ring was.
      if (from === null) {
        frameId.current = 0;
        commit(target);
        return;
      }
      const next = approach(from, target, 1 - Math.exp(-dt / TAU_MS));
      const done = gap(next, target) <= SETTLED_PX;
      commit(done ? target : next);
      if (done) {
        frameId.current = 0;
        return;
      }
      frameId.current = requestAnimationFrame(frame);
    };
    frameId.current = requestAnimationFrame(frame);
  }, [commit]);

  const follow = useCallback(() => {
    const to = measureRef.current();
    if (to === null) {
      commit(null);
      // A step with a target keeps looking for it; a modal step (welcome,
      // finish) and a closed tour spin nothing.
      if (wantsRef.current) start();
      else stop();
      return;
    }
    if (shown.current === null || reducedRef.current) {
      stop();
      commit(to);
      return;
    }
    start();
  }, [commit, start, stop]);

  // Nothing may outlive the component: a pending frame would `setState` on an
  // unmounted tour.
  useEffect(() => stop, [stop]);

  return { rect, follow };
}
